/**
 * OpBuilderForm — the changeset drawer's minimal "Add change" op-builder
 * (PIM Wave P5 Task 7). Stages ONE of six high-value, safe op types via
 * `POST /admin/catalog/changesets/{id}/operations`:
 *
 *   field.archive / field.unarchive, option.archive, category.archive,
 *   binding.create ("add field to category"), binding.update
 *   ("change binding requirement").
 *
 * This is DELIBERATELY a small subset of the server's full 27-entry
 * `ops.OP_TYPES` registry (`changesets/ops.py`) — no create-new-field-with-
 * `$ref`, no group/alias/need_mapping ops, no generic "any op type" form. A
 * full generic op-builder covering every op type is a documented follow-up
 * (see this task's report); the OTHER way a changeset gets populated at
 * scale is the transfer-import bundle (PIM Wave P5 Task 8, not this task).
 *
 * Plain controlled `useState` (no RHF/Zod) — mirrors `MatcherBuilder.tsx`'s
 * own "numeric_min/numeric_max/option_slugs are fully controlled" precedent,
 * amplified here since EVERY field in this form is dependent-select-driven
 * (op kind -> which pickers show -> what payload shape to send); a schema
 * that has to be rebuilt per kind change for a one-shot "stage this op" POST
 * (not a multi-field entity form with its own validation semantics) isn't
 * proportionate. Each of the 6 kinds reads its OWN entity list, so this
 * intentionally re-fetches (TanStack Query dedupes the shared caches with
 * `FieldLibraryPage`/`CategoryListPage`/`BindingEditorPage` if they happen to
 * be mounted, but does not assume they are).
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@garden/ui";
import { ADMIN_NS } from "@/features/admin/api";
import { useAllAttributeFields, useAttributeFieldOptions } from "@/features/admin/attribute-fields/hooks";
import { useAllAdminCategories } from "@/features/admin/categories/hooks";
import { useCategoryBindings } from "@/features/admin/category-bindings/hooks";
import { BINDING_REQUIREMENT_VALUES } from "@/features/admin/category-bindings/api";
import { useAppendChangesetOperation } from "@/features/admin/changesets/hooks";

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

const OP_KINDS = [
  "field.archive",
  "field.unarchive",
  "option.archive",
  "category.archive",
  "binding.create",
  "binding.update",
] as const;
type OpKind = (typeof OP_KINDS)[number];

interface OpBuilderFormProps {
  changesetId: string;
}

export function OpBuilderForm({ changesetId }: OpBuilderFormProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);

  const [kind, setKind] = useState<OpKind>("field.archive");
  const [fieldId, setFieldId] = useState("");
  const [optionId, setOptionId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [bindingId, setBindingId] = useState("");
  const [requirement, setRequirement] = useState<string>("required");

  const resetSelections = (): void => {
    setFieldId("");
    setOptionId("");
    setCategoryId("");
    setBindingId("");
    setRequirement("required");
  };

  const handleKindChange = (next: OpKind): void => {
    setKind(next);
    resetSelections();
  };

  const fieldsQuery = useAllAttributeFields();
  const fields = fieldsQuery.data ?? [];
  const categoriesQuery = useAllAdminCategories();
  const categories = categoriesQuery.data ?? [];

  const needsOptions = kind === "option.archive";
  const optionsQuery = useAttributeFieldOptions(needsOptions && fieldId !== "" ? fieldId : null);
  const options = (optionsQuery.data?.options ?? []).filter((o) => !o.is_archived);

  const needsCategoryBindings = kind === "binding.update";
  const bindingsQuery = useCategoryBindings(
    needsCategoryBindings && categoryId !== "" ? categoryId : null,
  );
  const bindings = bindingsQuery.data?.bindings ?? [];
  const fieldsById = new Map(fields.map((f) => [f.id, f]));

  const appendMutation = useAppendChangesetOperation();

  const activeFields = fields.filter((f) => !f.is_archived);
  const archivedFields = fields.filter((f) => f.is_archived);
  const activeCategories = categories.filter((c) => !c.is_archived);

  function buildOp(): { op_type: string; payload: Record<string, unknown> } | null {
    switch (kind) {
      case "field.archive":
      case "field.unarchive":
        return fieldId ? { op_type: kind, payload: { field_id: fieldId } } : null;
      case "option.archive":
        return fieldId && optionId
          ? { op_type: "option.archive", payload: { field_id: fieldId, option_id: optionId } }
          : null;
      case "category.archive":
        return categoryId ? { op_type: "category.archive", payload: { category_id: categoryId } } : null;
      case "binding.create":
        return categoryId && fieldId && requirement
          ? {
              op_type: "binding.create",
              payload: { category_id: categoryId, field_id: fieldId, requirement },
            }
          : null;
      case "binding.update":
        return categoryId && bindingId && requirement
          ? {
              op_type: "binding.update",
              payload: {
                category_id: categoryId,
                binding_id: bindingId,
                changes: { requirement },
              },
            }
          : null;
      default:
        return null;
    }
  }

  const op = buildOp();
  const canSubmit = op !== null && !appendMutation.isPending;

  const handleAdd = (): void => {
    if (!op) return;
    appendMutation.mutate(
      { id: changesetId, body: op },
      {
        onSuccess: () => {
          toast.success(t("pim.changesets.opBuilder.addSuccess"));
          resetSelections();
        },
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  return (
    <div
      data-testid="pim-changeset-op-builder"
      className="flex flex-col gap-3 rounded-[var(--g-radius)] border border-[var(--g-hairline)] p-3"
    >
      <h3 className="text-sm font-semibold text-[var(--g-primary-ink)]">
        {t("pim.changesets.opBuilder.title")}
      </h3>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="pim-changeset-op-kind" className="text-[13px] font-semibold text-foreground">
          {t("pim.changesets.opBuilder.kindLabel")}
        </label>
        <select
          id="pim-changeset-op-kind"
          data-testid="pim-changeset-op-kind"
          className={SELECT_CLASS}
          value={kind}
          onChange={(event) => handleKindChange(event.target.value as OpKind)}
        >
          {OP_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`pim.changesets.opBuilder.kind.${k}`)}
            </option>
          ))}
        </select>
      </div>

      {(kind === "field.archive" || kind === "field.unarchive") ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="pim-changeset-op-field" className="text-[13px] font-semibold text-foreground">
            {t("pim.changesets.opBuilder.fieldLabel")}
          </label>
          <select
            id="pim-changeset-op-field"
            data-testid="pim-changeset-op-field"
            className={SELECT_CLASS}
            value={fieldId}
            onChange={(event) => setFieldId(event.target.value)}
          >
            <option value="">{t("pim.changesets.opBuilder.fieldPlaceholder")}</option>
            {(kind === "field.archive" ? activeFields : archivedFields).map((field) => (
              <option key={field.id} value={field.id}>
                {field.name_ru}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {kind === "option.archive" ? (
        <>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pim-changeset-op-option-field" className="text-[13px] font-semibold text-foreground">
              {t("pim.changesets.opBuilder.fieldLabel")}
            </label>
            <select
              id="pim-changeset-op-option-field"
              data-testid="pim-changeset-op-option-field"
              className={SELECT_CLASS}
              value={fieldId}
              onChange={(event) => {
                setFieldId(event.target.value);
                setOptionId("");
              }}
            >
              <option value="">{t("pim.changesets.opBuilder.fieldPlaceholder")}</option>
              {activeFields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.name_ru}
                </option>
              ))}
            </select>
          </div>
          {fieldId ? (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pim-changeset-op-option" className="text-[13px] font-semibold text-foreground">
                {t("pim.changesets.opBuilder.optionLabel")}
              </label>
              <select
                id="pim-changeset-op-option"
                data-testid="pim-changeset-op-option"
                className={SELECT_CLASS}
                value={optionId}
                onChange={(event) => setOptionId(event.target.value)}
              >
                <option value="">{t("pim.changesets.opBuilder.optionPlaceholder")}</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label_ru}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </>
      ) : null}

      {kind === "category.archive" ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="pim-changeset-op-category" className="text-[13px] font-semibold text-foreground">
            {t("pim.changesets.opBuilder.categoryLabel")}
          </label>
          <select
            id="pim-changeset-op-category"
            data-testid="pim-changeset-op-category"
            className={SELECT_CLASS}
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            <option value="">{t("pim.changesets.opBuilder.categoryPlaceholder")}</option>
            {activeCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name_ru}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {kind === "binding.create" ? (
        <>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pim-changeset-op-binding-category" className="text-[13px] font-semibold text-foreground">
              {t("pim.changesets.opBuilder.categoryLabel")}
            </label>
            <select
              id="pim-changeset-op-binding-category"
              data-testid="pim-changeset-op-binding-category"
              className={SELECT_CLASS}
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">{t("pim.changesets.opBuilder.categoryPlaceholder")}</option>
              {activeCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name_ru}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pim-changeset-op-binding-field" className="text-[13px] font-semibold text-foreground">
              {t("pim.changesets.opBuilder.fieldLabel")}
            </label>
            <select
              id="pim-changeset-op-binding-field"
              data-testid="pim-changeset-op-binding-field"
              className={SELECT_CLASS}
              value={fieldId}
              onChange={(event) => setFieldId(event.target.value)}
            >
              <option value="">{t("pim.changesets.opBuilder.fieldPlaceholder")}</option>
              {activeFields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.name_ru}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pim-changeset-op-binding-requirement" className="text-[13px] font-semibold text-foreground">
              {t("pim.changesets.opBuilder.requirementLabel")}
            </label>
            <select
              id="pim-changeset-op-binding-requirement"
              data-testid="pim-changeset-op-binding-requirement"
              className={SELECT_CLASS}
              value={requirement}
              onChange={(event) => setRequirement(event.target.value)}
            >
              {BINDING_REQUIREMENT_VALUES.map((value) => (
                <option key={value} value={value}>
                  {t(`pim.bindings.requirement.${value}`)}
                </option>
              ))}
            </select>
          </div>
        </>
      ) : null}

      {kind === "binding.update" ? (
        <>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pim-changeset-op-update-category" className="text-[13px] font-semibold text-foreground">
              {t("pim.changesets.opBuilder.categoryLabel")}
            </label>
            <select
              id="pim-changeset-op-update-category"
              data-testid="pim-changeset-op-update-category"
              className={SELECT_CLASS}
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value);
                setBindingId("");
              }}
            >
              <option value="">{t("pim.changesets.opBuilder.categoryPlaceholder")}</option>
              {activeCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name_ru}
                </option>
              ))}
            </select>
          </div>
          {categoryId ? (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pim-changeset-op-binding" className="text-[13px] font-semibold text-foreground">
                {t("pim.changesets.opBuilder.bindingLabel")}
              </label>
              <select
                id="pim-changeset-op-binding"
                data-testid="pim-changeset-op-binding"
                className={SELECT_CLASS}
                value={bindingId}
                onChange={(event) => {
                  setBindingId(event.target.value);
                  const binding = bindings.find((b) => b.id === event.target.value);
                  if (binding) setRequirement(binding.requirement);
                }}
              >
                <option value="">{t("pim.changesets.opBuilder.bindingPlaceholder")}</option>
                {bindings.map((binding) => (
                  <option key={binding.id} value={binding.id}>
                    {fieldsById.get(binding.field_id)?.name_ru ?? binding.field_id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {bindingId ? (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pim-changeset-op-update-requirement" className="text-[13px] font-semibold text-foreground">
                {t("pim.changesets.opBuilder.requirementLabel")}
              </label>
              <select
                id="pim-changeset-op-update-requirement"
                data-testid="pim-changeset-op-update-requirement"
                className={SELECT_CLASS}
                value={requirement}
                onChange={(event) => setRequirement(event.target.value)}
              >
                {BINDING_REQUIREMENT_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {t(`pim.bindings.requirement.${value}`)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="flex justify-end pt-1">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          data-testid="pim-changeset-op-add"
          loading={appendMutation.isPending}
          disabled={!canSubmit}
          onClick={handleAdd}
          className="min-h-[44px]"
        >
          {t("pim.changesets.opBuilder.add")}
        </Button>
      </div>
    </div>
  );
}

export default OpBuilderForm;
